import { getQbeTrack } from '../configs/config';
import analytics from '../connectors/analytics';
import { EngagementTrackingLegacyPayload } from '../types';

const DATASET_TRACK = 'track';
const DATASET_TRACK_VALUE = 'trackValue';
const DATA_TRACK = '[data-track]';
const MASK = 'mask';
const CAPTURE = 'capture';
let isEngagementTrackingInitialised = false;

const errorPayload = (err: unknown): Record<string, unknown> => {
  return {
    event: 'gtm_error',
    eventAction: 'QAL - ET Library - Generic',
    eventLabel: err instanceof Error ? err.message : String(err),
    data: {},
  };
};

const trackEvents = (payload: EngagementTrackingLegacyPayload) => {
  //   window.dataLayer?.push(payload);
  analytics.trackQalInBuiltEngagementTracking(payload);
};

const formatPayload = (
  event: Record<string, unknown>
): EngagementTrackingLegacyPayload => {
  return {
    eventAction:
      typeof event.dataTrack === 'string' ? event.dataTrack : '(not set)',
    eventLabel:
      typeof event.value === 'string'
        ? event.value
        : JSON.stringify(event.value || '(not set)'),
    data: {
      ...event,
    }, // Ensure `data` exists
  };
};

const getValue = (
  dataset: DOMStringMap,
  target: HTMLElement,
  clickEvent?: string
): string => {
  if (dataset.trackValue === MASK) return 'data-track-value masked';
  if (dataset.trackValue === CAPTURE || clickEvent)
    return (target as HTMLInputElement).value || target.innerText;
  return 'data-track-value not found';
};

const getPayload = (
  eventName: string,
  target: HTMLElement,
  ele: HTMLElement,
  clickEvent?: string
): Record<string, unknown> => {
  let payload: Record<string, unknown> = {
    eventName,
    type: (target as HTMLInputElement).type,
    nodeName: target.nodeName,
    dataTrack: ele.dataset?.[DATASET_TRACK],
    dataTrackValue: ele.dataset?.[DATASET_TRACK_VALUE] || undefined,
    value: getValue(ele.dataset, target, clickEvent),
  };

  const qbeTrack = getQbeTrack();
  if (qbeTrack.meta?.dataPoints) {
    payload = { ...payload, ...qbeTrack.meta.dataPoints };
  }

  return payload;
};

const defaultChangeHandler = (eventName: string, target: HTMLElement) => {
  const ele = target.closest(DATA_TRACK);

  if (!ele) return;
  const inputPayload = getPayload(eventName, target, ele as HTMLElement);
  trackEvents(formatPayload(inputPayload));
};

const defaultClickHandler = (eventName: string, target: HTMLElement) => {
  let ele: HTMLElement | null = target.closest(DATA_TRACK);
  for (let i = 0; i < 2 && !ele; i++) {
    const parent = target.parentElement;
    if (!parent) break;
    target = parent;
    ele = target.closest(DATA_TRACK);
  }

  // Do not track clicks which have a data-track-value because this is handled
  // by the defaultChangeHandler above
  if (ele?.dataset?.[DATASET_TRACK_VALUE]) return;
  if (!ele || !/^(A|BUTTON|DIV|LABEL)$/i.test(ele.nodeName)) return;
  const clickPayload = getPayload(eventName, target, ele, 'click');
  trackEvents(formatPayload(clickPayload));
};

const attachEvent = (eventName: string) => {
  document.body.addEventListener(
    eventName,
    (event: Event) => {
      const target = event.target as HTMLElement;
      if (!target) return;

      if (
        eventName === 'change' &&
        /^(radio|checkbox)$/i.test((target as HTMLInputElement).type)
      )
        return;
      if (
        eventName === 'change' ||
        (eventName === 'click' &&
          /radio|checkbox/i.test((target as HTMLInputElement).type))
      ) {
        return defaultChangeHandler(eventName, target);
      }
      if (eventName === 'click') {
        return defaultClickHandler(eventName, target);
      }
    },
    true
  );
};

export const engagementTrackingInit = () => {
  if (typeof window === 'undefined') return;
  if (isEngagementTrackingInitialised) return;

  const qbeTrack = getQbeTrack();
  try {
    if (
      typeof qbeTrack.meta.enableAdsEngagementTracking !== 'undefined' &&
      qbeTrack.meta.enableAdsEngagementTracking !== false &&
      qbeTrack.meta.qal
    ) {
      return;
    }
    if (!Element.prototype.closest)
      throw new Error('Element.prototype.closest not supported');
    ['change', 'click', 'focusout'].forEach((event) => attachEvent(event));
    isEngagementTrackingInitialised = true;
  } catch (err) {
    // Mark as initialised to prevent repeated error-tracking on subsequent calls.
    isEngagementTrackingInitialised = true;
    analytics.trackCustomEvent(errorPayload(err));
  }
};
